import { useQuery } from '@apollo/client';
import React, { useCallback, useContext } from 'react';
import { createContext, useMemo } from 'react';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import mergeSort from '../../../../../Utils/mergeSort';
import { RutinSettingsContext } from '../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import resursTypQuery from './Queries/ResursTypQuery.gql';
import {
  ResursTypQuery,
  ResursTypQueryVariables,
} from './Queries/_QueryTypes/ResursTypQuery';
import {
  AktivitetstypQuery,
  AktivitetstypQueryVariables,
} from './Queries/_QueryTypes/AktivitetstypQuery';
import aktivitetstypQuery from './Queries/AktivitetstypQuery.gql';
import { TEDBOAktivitetTypEnum } from '../../../../../globalQueryTypes';

export interface IResursTyp {
  id: string;
  namn: string;
  foraldratyp: string;
  bastyp: string;
}

export interface IAktTypResursRubrik {
  id: string;
  posNr: number;
  foreslagenResTyp: string;
}

export interface IResursTypContext {
  isOfType: (resTyp: string, isTyp: string) => number;
  resTypes: () => IResursTyp[];
  aktTypRubriker: (
    aktTyp: string,
    bokngruppId: string
  ) => IAktTypResursRubrik[];
}

type IAktTypTranslation = {
  [name in TEDBOAktivitetTypEnum]: string;
} & { [key: string]: string | undefined };

const translateAktTyp: IAktTypTranslation = {
  [TEDBOAktivitetTypEnum.AKTOVRIGT]: 'Ö',
  [TEDBOAktivitetTypEnum.BEGRAVNING]: 'B',
  [TEDBOAktivitetTypEnum.DOP]: 'D',
  [TEDBOAktivitetTypEnum.GRAVSATTNING]: 'G',
  [TEDBOAktivitetTypEnum.GUDSTJANST]: 'U',
  [TEDBOAktivitetTypEnum.KONFIRMATION]: 'K',
  [TEDBOAktivitetTypEnum.LOKALUTHYRNING]: 'L',
  [TEDBOAktivitetTypEnum.OUTLOOK]: 'O',
  [TEDBOAktivitetTypEnum.PLANERING]: 'P',
  [TEDBOAktivitetTypEnum.SNABBOKNING]: 'S',
  [TEDBOAktivitetTypEnum.VIGSEL]: 'V',
};

const defaultValues: IResursTypContext = {
  isOfType: () => 0,
  resTypes: () => [],
  aktTypRubriker: () => [],
};

export const ResursTypContext = createContext<IResursTypContext>(defaultValues);

export const ResursTypContextProvider: React.FC<
  React.PropsWithChildren<unknown>
> = props => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const routineID = routineAvailabilityContext.activeRoutine?.routine.rutinID;

  const { data: resurstyp_data } = useQuery<
    ResursTypQuery,
    ResursTypQueryVariables
  >(resursTypQuery, {
    variables: {
      dbname: activeDatabaseContext.activeDatabase ?? 'noDBYet',
      routineID: routineID,
    },
    skip: !activeDatabaseContext.activeDatabase,
  });

  const { data: aktivitetstyp_data } = useQuery<
    AktivitetstypQuery,
    AktivitetstypQueryVariables
  >(aktivitetstypQuery, {
    variables: {
      dbname: activeDatabaseContext.activeDatabase ?? 'noDBYet',
    },
    skip: !activeDatabaseContext.activeDatabase,
  });

  const isOfType = useCallback(
    (resTyp: string, isTyp: string) => {
      let resourceTypes: Map<string, string> = new Map();
      if (resurstyp_data !== undefined) {
        resurstyp_data?.db?.BOResurstyp_All?.forEach(rTyp => {
          resourceTypes.set(rTyp.BO_RESURSTYP_NAMN, rTyp.FORALDRATYP ?? '');
        });
      }
      const localIsOfType = (
        resourceTypes: Map<string, string>,
        resTyp: string,
        isTyp: string,
        depth: number
      ): number =>
        resTyp
          ? resTyp === isTyp
            ? depth
            : localIsOfType(
                resourceTypes,
                resourceTypes.get(resTyp) ?? '',
                isTyp,
                depth + 1
              )
          : 0;
      return localIsOfType(resourceTypes, resTyp, isTyp, 1);
    },
    [resurstyp_data]
  );

  const resTypes = useCallback(
    () =>
      mergeSort(
        resurstyp_data?.db?.BOResurstyp_All ?? [],
        (a, b) => (a.PRIO_ORDNING ?? 1000) - (b.PRIO_ORDNING ?? 1000)
      ).map(resTyp => ({
        id: resTyp.id ?? '',
        namn: resTyp.BO_RESURSTYP_NAMN ?? '',
        foraldratyp: resTyp.FORALDRATYP ?? '',
        bastyp: resTyp.Bastyp ?? '',
      })),
    [resurstyp_data]
  );

  const aktTypRubriker = useCallback(
    (aktTyp: string, bokngruppId: string) =>
      aktivitetstyp_data?.db?.BOAktivitetstyp_All?.find(
        aTyp => aTyp.FORK === translateAktTyp[aktTyp]
      )
        ?._Associations?.AktTypResrubrik_All?.filter(
          resrub => resrub._Associations?.Bokningsgrupp?.id === bokngruppId
        )
        .map(resrub => ({
          id: resrub._Associations?.Bokningsgrupp?.id ?? '',
          posNr: resrub.POSNR ?? 0,
          foreslagenResTyp: resrub._Associations?.Resrubrik?.ForeslResTyp ?? '',
        })) || [],
    [aktivitetstyp_data]
  );

  const value: IResursTypContext = useMemo(
    () => ({
      isOfType: isOfType,
      resTypes: resTypes,
      aktTypRubriker: aktTypRubriker,
    }),
    [isOfType, resTypes, aktTypRubriker]
  );

  return (
    <ResursTypContext.Provider value={value}>
      {props.children}
    </ResursTypContext.Provider>
  );
};
